"use client"

import * as React from "react"
import { Dialog as DialogBase } from "@base-ui-components/react/dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

function Sheet({
  children,
  open,
  defaultOpen,
  onOpenChange,
  modal,
}: {
  children: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
}) {
  return (
    <DialogBase.Root
      data-slot="sheet"
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      {children}
    </DialogBase.Root>
  )
}

function SheetTrigger({
  asChild,
  children,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  if (asChild) {
    return (
      <DialogBase.Trigger
        data-slot="sheet-trigger"
        render={children as React.ReactElement}
        {...props}
      />
    )
  }

  return (
    <DialogBase.Trigger data-slot="sheet-trigger" {...props}>
      {children}
    </DialogBase.Trigger>
  )
}

function SheetClose({
  asChild,
  children,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  if (asChild) {
    return (
      <DialogBase.Close
        data-slot="sheet-close"
        render={children as React.ReactElement}
        {...props}
      />
    )
  }

  return (
    <DialogBase.Close data-slot="sheet-close" {...props}>
      {children}
    </DialogBase.Close>
  )
}

function SheetPortal({
  children,
  ...props
}: {
  children: React.ReactNode
  container?: Element | null
}) {
  return (
    <DialogBase.Portal data-slot="sheet-portal" {...props}>
      {children}
    </DialogBase.Portal>
  )
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <DialogBase.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "right",
  ...props
}: React.ComponentProps<"div"> & {
  side?: "top" | "right" | "bottom" | "left"
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogBase.Popup
        data-slot="sheet-content"
        className={cn(
          "bg-background data-[open]:animate-in data-[ending-style]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[ending-style]:duration-300 data-[open]:duration-500",
          side === "right" &&
            "data-[ending-style]:slide-out-to-right data-[open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" &&
            "data-[ending-style]:slide-out-to-left data-[open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" &&
            "data-[ending-style]:slide-out-to-top data-[open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-[ending-style]:slide-out-to-bottom data-[open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className
        )}
        {...props}
      >
        {children}
        <DialogBase.Close
          data-slot="sheet-close-default"
          className="ring-offset-background focus:ring-ring data-[open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </DialogBase.Close>
      </DialogBase.Popup>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogBase.Title>) {
  return (
    <DialogBase.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogBase.Description>) {
  return (
    <DialogBase.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
